/**
 * ABOUTME: Send view for SmartQuote with adaptive UI based on recipient count
 * ABOUTME: Handles contact info collection and email/SMS sending with platform optimizations
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Phone, CheckCircle, Clipboard, User, ArrowLeft, ExternalLink, MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tradesperson } from '@/lib/types';
import { toast } from 'sonner';
import { debug } from '@/lib/utils';
import {
  validateContactForm, generateMailtoUrl, generateSmsLink,
  isMobilePlatform, isSafari, openUrl, hasPlaceholders
} from './smartQuoteUtils';

interface SmartQuoteSendViewProps {
  message: string;
  selectedTradespeople: string[];
  tradespeople: Tradesperson[];
  initialName?: string;
  sendingStatus: Record<string, 'pending' | 'sending' | 'sent' | 'error'>;
  setSendingStatus: React.Dispatch<React.SetStateAction<Record<string, 'pending' | 'sending' | 'sent' | 'error'>>>;
  sendMethod: Record<string, 'email' | 'messages' | null>;
  setSendMethod: React.Dispatch<React.SetStateAction<Record<string, 'email' | 'messages' | null>>>;
  onRecordEmail: (tradeId: string) => Promise<boolean>;
  onRecordSMS: (tradeId: string) => Promise<boolean>;
  onMessageChange: (message: string) => void;
  onComplete: () => void;
  onBack: () => void;
  onActionButtonChange?: (show: boolean) => void;
}


export function SmartQuoteSendView({
  message,
  selectedTradespeople,
  tradespeople,
  initialName = '',
  sendingStatus,
  setSendingStatus,
  sendMethod,
  setSendMethod,
  onRecordEmail,
  onRecordSMS,
  onMessageChange,
  onComplete,
  onBack,
  onActionButtonChange
}: SmartQuoteSendViewProps) {
  // Contact details state - simplified to only name
  const [name, setName] = useState(initialName || '');
  const [contactError, setContactError] = useState<string | null>(null);

  // Send options state - removed sendingStatus and sendMethod as they're now props
  const [selectedRecipients, setSelectedRecipients] = useState<Record<string, boolean>>(
    Object.fromEntries(selectedTradespeople.map(id => [id, true]))
  );
  const [isSendingAll, setIsSendingAll] = useState(false);

  // Get filtered tradespeople that match our selected IDs
  const selectedTradesData = tradespeople.filter(
    trade => selectedTradespeople.includes(trade.id)
  );

  // Get contacts with at least one contact method
  const contactsWithMethods = selectedTradesData.filter(trade =>
    (trade.email && trade.email.trim()) || (trade.phone && trade.phone.trim())
  );

  // Check if all contacts have been sent and notify parent
  useEffect(() => {
    if (onActionButtonChange && contactsWithMethods.length > 0) {
      const allSent = contactsWithMethods.every(t => sendingStatus[t.id] === 'sent');
      onActionButtonChange(allSent);
    }
  }, [sendingStatus, contactsWithMethods, onActionButtonChange]);


  // Copy the message to clipboard
  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success('Message copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy message');
      debug.error('Copy message error:', error);
    }
  };

  // Send email to specific contact
  const handleSendEmailToContact = (tradeId: string) => {
    // Check for placeholders first
    if (hasPlaceholders(message)) {
      toast.error('Please fill in all required information before sending');
      return;
    }

    const validation = validateContactForm(name);
    if (!validation.isValid) {
      setContactError(validation.errorMessage);
      return;
    }

    try {
      // Find the trade
      const trade = tradespeople.find(t => t.id === tradeId);
      if (!trade || !trade.email) {
        toast.error('Email address not available');
        return;
      }

      // Create mailto URL for single contact
      const mailtoUrl = `mailto:${trade.email}?subject=Quote Request&body=${encodeURIComponent(message)}`;

      // Open the email client
      const success = openUrl(mailtoUrl);
      if (!success) {
        toast.error('Failed to open email client. Try the "Copy Message" option.');
        return;
      }

      // IMMEDIATELY mark as sent and record method
      setSendingStatus(prev => ({
        ...prev,
        [tradeId]: 'sent'
      }));

      setSendMethod(prev => ({
        ...prev,
        [tradeId]: 'email'
      }));

      // Record in database (async)
      onRecordEmail(tradeId)
        .then(() => {
          debug.log('Email sending action recorded in database');
        })
        .catch(err => {
          debug.error('Failed to record email action in database:', err);
        });

      // Show success message
      toast.success('Email client opened - return here for next one');

      return true;
    } catch (error) {
      debug.error('Error opening email client:', error);
      toast.error('Failed to open email client. Try the "Copy Message" option.');
      return false;
    }
  };

  // Send text using direct DOM approach
  const handleSendText = (tradeId: string) => {
    // Check for placeholders first
    if (hasPlaceholders(message)) {
      toast.error('Please fill in all required information before sending');
      return false;
    }

    const validation = validateContactForm(name);
    if (!validation.isValid) {
      setContactError(validation.errorMessage);
      return false;
    }

    try {
      // Find the trade
      const trade = tradespeople.find(t => t.id === tradeId);
      if (!trade || !trade.phone) {
        toast.error('Phone number not available');
        return false;
      }

      // Generate SMS link
      const smsLink = generateSmsLink(trade.phone, message);
      if (!smsLink) {
        toast.error('Could not create SMS link');
        return false;
      }

      // Open the URL using the direct DOM approach
      const success = openUrl(smsLink);
      if (!success) {
        toast.error('Failed to open Messages app. Try the "Copy Message" option.');
        return false;
      }

      // IMMEDIATELY mark as sent and record method
      setSendingStatus(prev => ({
        ...prev,
        [tradeId]: 'sent'
      }));

      setSendMethod(prev => ({
        ...prev,
        [tradeId]: 'messages'
      }));

      // Record a successful action in the database (async, don't wait)
      onRecordSMS(tradeId)
        .then(() => {
          debug.log('SMS sending action recorded in database');
        })
        .catch(err => {
          debug.error('Failed to record SMS action in database:', err);
        });

      // Show success message
      toast.success('Messages app opened - return here for next one');

      // Check if all contacts are complete
      const allContactsComplete = contactsWithMethods.every(
        contact => sendingStatus[contact.id] === 'sent' || contact.id === tradeId
      );

      if (allContactsComplete) {
        // All done - show completion after a short delay
        setTimeout(() => {
          toast.success('All messages sent! 🎉');
        }, 1500);
      }

      return true;
    } catch (error) {
      debug.error(`Error opening SMS app for trade ${tradeId}:`, error);
      toast.error('Failed to open Messages app. Try the "Copy Message" option.');
      return false;
    }
  };

  // Email-like preview UI - feels like finished result
  const renderEmailPreviewUI = () => {
    return (
      <div className="bg-white h-full flex flex-col">
        {/* Send Actions Header - replaces TO line */}
        <div className="px-4 py-3">
          {/* No contact methods available */}
          {contactsWithMethods.length === 0 && (
            <div className="text-center py-2">
              <div className="text-gray-600 mb-2 text-sm">
                No contact information available
              </div>
              <Button
                onClick={handleCopyMessage}
                variant="outline"
                className="text-sm"
              >
                <Clipboard className="h-4 w-4 mr-2" />
                Copy Message Instead
              </Button>
            </div>
          )}

          {/* Send to Tradespeople Section */}
          {contactsWithMethods.length > 0 && (
            <div>
              <div className="px-4 py-2 border-b border-gray-100">
                <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                  Send to Tradespeople
                </h2>
              </div>
              <div className="bg-white">
              {contactsWithMethods.map(trade => {
                const status = sendingStatus[trade.id] || 'pending';
                const method = sendMethod[trade.id];
                const hasEmail = trade.email && trade.email.trim();
                const hasMessages = trade.phone && trade.phone.trim();

                // If already sent, show completed state
                if (status === 'sent') {
                  return (
                    <div
                      key={trade.id}
                      className="w-full py-4 px-0 flex items-center justify-between border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center px-4">
                        {method === 'email' ? (
                          <Mail className="h-4 w-4 mr-3 text-blue-600" />
                        ) : (
                          <MessageSquare className="h-4 w-4 mr-3 text-green-600" />
                        )}
                        <div className="text-left">
                          <div className="font-medium text-gray-900 text-base">
                            {trade.org || trade.contact}
                          </div>
                          <div className="text-xs text-gray-500">
                            Sent via {method === 'email' ? 'email' : 'messages'}
                          </div>
                        </div>
                      </div>
                      <div className="px-4">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      </div>
                    </div>
                  );
                }

                // Show available options - single long button with name and options
                return (
                  <div
                    key={trade.id}
                    className="w-full py-4 px-0 flex items-center justify-between hover:bg-gray-50 active:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0"
                  >
                    {/* Contact name on left */}
                    <div className="flex-1 px-4">
                      <div className="font-medium text-gray-900 text-base">
                        {trade.org || trade.contact}
                      </div>
                    </div>

                    {/* Send options on right */}
                    <div className="flex gap-3">
                      {/* Email button */}
                      {hasEmail && (
                        <Button
                          onClick={() => handleSendEmailToContact(trade.id)}
                          size="sm"
                          className="w-10 h-10 p-0 rounded-full flex items-center justify-center bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white transition-all shadow-sm"
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                      )}

                      {/* Messages button */}
                      {hasMessages && (
                        <Button
                          onClick={() => handleSendText(trade.id)}
                          size="sm"
                          className="w-10 h-10 p-0 rounded-full flex items-center justify-center bg-green-500 hover:bg-green-600 active:bg-green-700 text-white transition-all shadow-sm"
                        >
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}

              <div className="text-xs text-gray-500 px-4 py-3 bg-gray-50 text-center">
                Choose email or messages for each contact
              </div>
              </div>
            </div>
          )}

        </div>

        {/* Message body */}
        <div className="flex-1 px-4 py-4 overflow-y-auto">
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="text-base text-gray-800 whitespace-pre-wrap leading-relaxed">
              {message}
            </div>
          </div>

          {/* Add name field if not provided */}
          {!name.trim() && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Add your name to the message:
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setContactError(null);
                  }}
                  placeholder="Your name"
                  className="w-full pl-10 py-3 border border-gray-200 rounded-lg text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <User className="h-4 w-4" />
                </div>
              </div>
              {contactError && (
                <div className="text-red-500 text-sm mt-1">{contactError}</div>
              )}
            </div>
          )}
        </div>

      </div>
    );
  };


  return (
    <motion.div
      key="smart-quote-send-view"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 5 }}
      transition={{ type: "spring", stiffness: 400, damping: 35 }}
      className="h-full flex flex-col bg-white"
    >
      {/* iOS-style header with back button */}
      <div className="flex-shrink-0 px-2 py-2 border-b border-gray-100">
        <div className="flex items-center justify-between h-11">
          <Button
            onClick={onBack}
            variant="ghost"
            className="flex items-center text-purple-600 hover:text-purple-800 p-2 -ml-2"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span className="text-base">Edit</span>
          </Button>

          <h1 className="text-lg font-semibold text-gray-900">
            Your Message
          </h1>

          <div className="w-16"></div> {/* Spacer for balance */}
        </div>
      </div>

      {/* Email preview interface */}
      <div className="flex-1 min-h-0">
        {renderEmailPreviewUI()}
      </div>
    </motion.div>
  );
}
